if (!customElements.get('sprung-reviews')) {
    customElements.define('sprung-reviews', class extends HTMLElement {
      connectedCallback() {
        queueMicrotask(() => {
          if (!this.isConnected || this.controller) return;
          this.viewport = this.querySelector('[data-viewport]');
          if (!this.viewport) return;
          this.controller = new AbortController();
          this.cards = [...this.querySelectorAll('[data-card]')];
          this.filters = [...this.querySelectorAll('[data-filter]')];
          this.prev = this.querySelector('[data-prev]');
          this.next = this.querySelector('[data-next]');
          this.range = this.querySelector('[data-range]');
          this.status = this.querySelector('[data-status]');
          this.empty = this.querySelector('[data-empty]');
          this.reset = this.querySelector('[data-reset]');
          this.activeFilter = 'all';
          const on = (node, event, handler, options = {}) => node.addEventListener(event, handler, { ...options, signal: this.controller.signal });
          this.querySelectorAll('[data-controls]').forEach(node => node.hidden = false);
          on(this, 'click', event => {
            const filter = event.target.closest('[data-filter]');
            if (filter) this.filter(filter.dataset.filter);
            if (event.target.closest('[data-prev]')) this.move(-1);
            if (event.target.closest('[data-next]')) this.move(1);
            if (event.target.closest('[data-reset]')) { this.filter('all'); this.filters[0].focus(); }
          });
          on(this.viewport, 'scroll', () => {
            this.sync();
            clearTimeout(this.scrollTimer);
            this.scrollTimer = setTimeout(() => { this.targetPosition = null; this.sync(); }, 180);
          }, { passive: true });
          on(this.viewport, 'wheel', () => { this.targetPosition = null; }, { passive: true });
          on(this.viewport, 'pointerdown', () => { this.targetPosition = null; }, { passive: true });
          on(this.viewport, 'keydown', event => {
            if (event.target !== this.viewport || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
            event.preventDefault();
            if (event.key === 'Home') this.scrollToPosition(0);
            else if (event.key === 'End') this.scrollToPosition(this.maxScroll);
            else this.move((event.key === 'ArrowRight' ? 1 : -1) * (this.isRtl ? -1 : 1));
          });
          on(this, 'focusin', event => {
            const card = event.target.closest('[data-card]');
            if (card) { this.targetPosition = null; card.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'instant' }); }
          });
          on(this, 'shopify:block:select', event => {
            const card = this.cards.find(item => item.dataset.blockId === event.detail?.blockId) || event.target.closest('[data-card]');
            if (!card) return;
            if (card.hidden) this.filter('all');
            card.scrollIntoView({ block: 'nearest', inline: 'start', behavior: 'instant' });
          });
          this.observer = new ResizeObserver(() => { this.targetPosition = null; this.sync(); });
          this.observer.observe(this.viewport);
          this.filter('all', false);
        });
      }
      disconnectedCallback() {
        this.controller?.abort();
        this.observer?.disconnect();
        clearTimeout(this.scrollTimer);
        this.controller = null;
      }
      get isRtl() { return getComputedStyle(this.viewport).direction === 'rtl'; }
      get position() { return Math.abs(this.viewport.scrollLeft); }
      get maxScroll() { return Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth); }
      format(template, values) { return Object.entries(values).reduce((text, [key, value]) => text.replaceAll(`[${key}]`, value), template); }
      filter(category, announce = true) {
        this.activeFilter = category;
        this.cards.forEach(card => card.hidden = category !== 'all' && card.dataset.category !== category);
        this.visibleCards = this.cards.filter(card => !card.hidden);
        this.filters.forEach(button => button.setAttribute('aria-pressed', String(button.dataset.filter === category)));
        const count = this.visibleCards.length;
        this.empty.hidden = count > 0;
        this.viewport.hidden = count === 0;
        this.reset.hidden = category === 'all';
        this.querySelector('[data-count]').textContent = this.format(this.dataset.countLabel, { count });
        this.targetPosition = null;
        this.viewport.scrollTo({ left: 0, behavior: 'instant' });
        this.sync();
        if (announce) this.status.textContent = this.format(this.dataset.countLabel, { count });
      }
      scrollToPosition(position) {
        this.targetPosition = Math.max(0, Math.min(position, this.maxScroll));
        const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.viewport.scrollTo({ left: this.targetPosition * (this.isRtl ? -1 : 1), behavior: reduce ? 'instant' : 'smooth' });
        this.sync();
      }
      move(direction) {
        if (!this.visibleCards.length) return;
        const gap = parseFloat(getComputedStyle(this.querySelector('.sr__track')).gap) || 0;
        this.scrollToPosition((this.targetPosition ?? this.position) + direction * (this.visibleCards[0].getBoundingClientRect().width + gap));
      }
      sync() {
        const cards = this.visibleCards || [];
        const position = this.targetPosition ?? this.position;
        this.prev.disabled = cards.length === 0 || position <= 2;
        this.next.disabled = cards.length === 0 || position >= this.maxScroll - 2;
        const bounds = this.viewport.getBoundingClientRect();
        const shown = cards.map((card, index) => ({ index, rect: card.getBoundingClientRect() })).filter(({ rect }) => Math.min(bounds.right, rect.right) - Math.max(bounds.left, rect.left) >= rect.width * .5);
        const start = cards.length ? (shown[0]?.index ?? 0) + 1 : 0;
        const end = cards.length ? (shown.at(-1)?.index ?? 0) + 1 : 0;
        this.range.textContent = this.format(this.dataset.rangeLabel, { start, end, count: cards.length });
      }
    });
  }
